import { ArrowBack as BackIcon, Info as InfoIcon } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { getFactCheckItem, updateFactCheckItem } from '../services/factCheckItemService';
import {
  getUploadJob,
  type ProcessingCallbacks,
  type ProcessingResult,
  type ProcessingStatus,
  startStatusListener,
  type UploadCallbacks,
  updateProcessingCallbacks,
  updateUploadCallbacks,
} from '../services/videoUploadService';
import { IMG } from '../theme/assets';

const VideoProcessing: React.FC = () => {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const tokens = useThemeColors();

  // 状態管理
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [noFactsFound, setNoFactsFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemTitle, setItemTitle] = useState<string>('');

  // 案件ID（遷移元からの情報を優先、なければ後で取得）
  const workspaceId = (location.state as any)?.workspaceId || null;

  // コールバックの定義
  const uploadCallbacks = useMemo<UploadCallbacks>(
    () => ({
      onProgress: (uploaded: number, total: number) => {
        setUploadProgress((uploaded / total) * 100);
      },
      onComplete: (id: string, fileUrl: string) => {
        console.log('✅ アップロード完了:', id, fileUrl);
        // アップロード完了後、処理ステータスの監視は既に service 側で startStatusListener が呼ばれていれば継続される
      },
      onError: (id: string, err: Error) => {
        console.error('❌ アップロードエラー:', id, err);
        setError(`アップロードエラー: ${err.message}`);
      },
    }),
    [],
  );

  const processingCallbacks = useMemo<ProcessingCallbacks>(
    () => ({
      onStatusUpdate: (status: ProcessingStatus) => {
        if (status.itemId === itemId) {
          console.log('📊 処理ステータス更新:', status);
          setProcessingStatus(status);
        }
      },
      onComplete: async (id: string, result: ProcessingResult) => {
        if (id === itemId) {
          console.log('✅ 処理完了:', id, result);

          try {
            await updateFactCheckItem(id, {
              status: 'extracted',
              processingStage: null,
              facts: result.facts as any,
              total_facts: result.totalFacts,
              verified_facts: 0,
            });
          } catch (err) {
            console.error('❌ 保存エラー:', err);
          }

          if (result.totalFacts === 0) {
            setNoFactsFound(true);
          } else {
            // 抽出が終わったらそのまま確認画面へ進む。以前は一覧に戻していたが、
            // 待っていたユーザーが結果に辿り着けずドキュメント側とも挙動が違っていた。
            navigate(`/workflow/${itemId}`, { replace: true });
          }
        }
      },
      onError: (id: string, errorMsg: string) => {
        if (id === itemId) {
          console.error('❌ 処理エラー:', id, errorMsg);
          setError(`処理エラー: ${errorMsg}`);
        }
      },
    }),
    [itemId, navigate],
  );

  useEffect(() => {
    if (!itemId || !user) return;

    // アイテム情報の取得
    const loadItem = async () => {
      try {
        const item = await getFactCheckItem(itemId);
        if (item) {
          setItemTitle(item.title);
          // すでに完了している場合のハンドリング
          if (item.status === 'completed' || item.status === 'error') {
            navigate(`/results/${itemId}`, { replace: true });
          } else if (item.status === 'extracted' && !item.processingStage) {
            // 抽出済みで再訪した場合も確認画面へ送る（完了時の遷移先と揃える）
            navigate(`/workflow/${itemId}`, { replace: true });
          }
        }
      } catch (err) {
        console.error('Failed to load item:', err);
      }
    };
    loadItem();

    // 現在のアップロードジョブを確認して進捗を初期化
    const job = getUploadJob(itemId);
    if (job) {
      setUploadProgress((job.uploadedBytes / job.totalBytes) * 100);
    }

    // コールバックの接続
    updateUploadCallbacks(itemId, uploadCallbacks);

    // ステータス監視の開始（既に監視中の場合はコールバックが更新される）
    startStatusListener(itemId, processingCallbacks);

    return () => {
      // 画面を離れる際はコールバックを解除（setState防止）
      updateUploadCallbacks(itemId, null);
      updateProcessingCallbacks(itemId, null);
    };
  }, [itemId, user, navigate, uploadCallbacks, processingCallbacks]);

  const handleGoBack = () => {
    if (workspaceId) {
      navigate(`/share/${workspaceId}`);
    } else {
      navigate('/');
    }
  };

  const stage = processingStatus?.stage;
  const isUploading = !processingStatus || stage === 'pending';
  const isCompleted = stage === 'completed';
  const isError = Boolean(error) || stage === 'error';
  const progress = Math.round(isUploading ? uploadProgress : (processingStatus?.progress ?? 0));

  const STAGES = [
    { label: 'アップロード', icon: IMG.create.upload, desc: '動画ファイルをサーバーへ送信' },
    { label: '文字起こし', icon: IMG.process.transcribe, desc: '音声をテキストに変換（ASR）' },
    { label: 'ファクト抽出', icon: IMG.process.extract, desc: '文字起こしから主張を抽出' },
    { label: '完了', icon: IMG.process.complete, desc: '検証する主張の準備が完了' },
  ];
  let cur = 0;
  if (isUploading) cur = 0;
  else if (stage === 'uploaded' || stage === 'transcribing') cur = 1;
  else if (stage === 'extracting') cur = 2;
  else if (stage === 'completed') cur = 3;
  else if (stage === 'error') cur = 2;

  const stageStatus = (i: number): 'done' | 'active' | 'error' | 'pending' => {
    if (isError) return i < cur ? 'done' : i === cur ? 'error' : 'pending';
    if (isCompleted) return 'done';
    return i < cur ? 'done' : i === cur ? 'active' : 'pending';
  };

  const heroTitle = isError
    ? '処理エラー'
    : isCompleted
      ? '処理完了！'
      : isUploading
        ? 'アップロード中…'
        : stage === 'transcribing'
          ? '文字起こし中…'
          : stage === 'extracting'
            ? 'ファクト抽出中…'
            : '処理開始中…';
  const heroSub = isError
    ? (error ?? 'エラーが発生しました。再試行してください。')
    : isCompleted
      ? '検証する主張の確認に進めます。'
      : (processingStatus?.message ??
        (isUploading ? '動画ファイルをアップロードしています。' : '処理を進めています。'));

  const barColor = isError
    ? 'linear-gradient(90deg, #dc2626, #f87171)'
    : isCompleted
      ? 'linear-gradient(90deg, #16a34a, #34d399)'
      : `linear-gradient(90deg, ${tokens.teal}, #34d399)`;
  const pctColor = isError ? '#dc2626' : isCompleted ? '#16a34a' : tokens.teal;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: { xs: 2, md: 4 } }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, mb: 2.5 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={handleGoBack}
          sx={{
            background: tokens.paper,
            border: `1px solid ${tokens.line}`,
            borderRadius: '11px',
            px: 1.75,
            py: 0.9,
            fontSize: 13,
            fontWeight: 700,
            color: tokens.ink,
            textTransform: 'none',
            '&:hover': { background: tokens.paper, borderColor: tokens.teal },
          }}
        >
          マイページに戻る
        </Button>
        <Typography sx={{ fontSize: 20, fontWeight: 900, color: 'text.primary' }}>
          動画を処理中:{' '}
          <Box component="span" sx={{ color: tokens.teal }}>
            {itemTitle || '...'}
          </Box>
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {noFactsFound ? (
          /* ファクト0件 */
          <Box
            sx={{
              textAlign: 'center',
              background: `linear-gradient(135deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
              border: `1px solid ${tokens.line}`,
              borderRadius: '16px',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              component="img"
              src={IMG.process.complete}
              alt=""
              sx={{ width: 88, height: 88, objectFit: 'contain', mb: 1 }}
            />
            <Typography sx={{ fontSize: 20, fontWeight: 900, color: 'text.primary' }}>
              検証可能なファクトが見つかりませんでした
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1, lineHeight: 1.6 }}>
              この動画には、客観的に検証可能な主張が含まれていませんでした。
            </Typography>
            <Alert severity="info" sx={{ textAlign: 'left', mt: 2.5, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                対象となる例
              </Typography>
              <Typography variant="body2" component="div">
                ✅ 統計・数値 / 歴史的事実 / 公式発表 / 人物プロフィール
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
                対象外の例
              </Typography>
              <Typography variant="body2" component="div">
                ❌ 個人的な意見・感想 / 主観的評価 / 未来予測 / 相談内容
              </Typography>
            </Alert>
            <Button
              onClick={handleGoBack}
              sx={{
                mt: 2.5,
                background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                color: '#fff',
                fontWeight: 800,
                borderRadius: '12px',
                px: 3,
                py: 1.1,
                textTransform: 'none',
              }}
            >
              一覧に戻る
            </Button>
          </Box>
        ) : (
          <>
            {/* 進捗ヒーロー */}
            <Box
              sx={{
                textAlign: 'center',
                background: isError
                  ? 'linear-gradient(135deg, #fdeaea, #fff3f3)'
                  : `linear-gradient(135deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
                border: `1px solid ${tokens.line}`,
                borderRadius: '16px',
                p: { xs: 3, md: 3.5 },
              }}
            >
              <Box
                component="img"
                src={isCompleted ? IMG.process.complete : IMG.owl.clipboard}
                alt=""
                sx={{
                  width: 92,
                  height: 92,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 8px 16px rgba(30,30,70,0.18))',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  mt: 1,
                }}
              >
                {!isError && !isCompleted && (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      border: `3px solid ${tokens.teal}40`,
                      borderTopColor: tokens.teal,
                      borderRadius: '50%',
                      animation: 'vpspin 0.9s linear infinite',
                      '@keyframes vpspin': { to: { transform: 'rotate(360deg)' } },
                    }}
                  />
                )}
                <Typography sx={{ fontSize: 21, fontWeight: 900, color: 'text.primary' }}>
                  {heroTitle}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, lineHeight: 1.55 }}
              >
                {heroSub}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2.5 }}>
                <Box
                  sx={{
                    flex: 1,
                    height: 13,
                    borderRadius: '8px',
                    background: '#dfe4ee',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${progress}%`,
                      background: barColor,
                      borderRadius: '8px',
                      transition: 'width 0.5s ease',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 900,
                    color: pctColor,
                    minWidth: 48,
                    textAlign: 'right',
                  }}
                >
                  {progress}%
                </Typography>
              </Box>
              {processingStatus?.durationSec ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 2,
                    background: '#fff',
                    border: `1px solid ${tokens.line}`,
                    borderRadius: 999,
                    px: 1.75,
                    py: 0.75,
                    fontSize: 12,
                    fontWeight: 800,
                    color: 'text.secondary',
                  }}
                >
                  動画の長さ {Math.floor(processingStatus.durationSec / 60)}分
                  {Math.floor(processingStatus.durationSec % 60)}秒
                </Box>
              ) : null}
            </Box>

            {/* ステージトラッカー */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: `1px solid ${tokens.line}`,
                borderRadius: '13px',
                px: 2.5,
                py: 0.5,
              }}
            >
              {STAGES.map((g, i) => {
                const st = stageStatus(i);
                return (
                  <Box
                    key={g.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.75,
                      py: 1.75,
                      borderBottom: i < STAGES.length - 1 ? `1px solid ${tokens.line}` : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 50,
                        height: 50,
                        borderRadius: '14px',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        background:
                          st === 'done'
                            ? '#e7f6ed'
                            : st === 'active'
                              ? '#eafaf6'
                              : st === 'error'
                                ? '#fdeaea'
                                : '#f1f3f7',
                        boxShadow:
                          st === 'active'
                            ? `0 0 0 3px ${tokens.teal}2e`
                            : st === 'error'
                              ? '0 0 0 3px rgba(220,38,38,0.16)'
                              : 'none',
                      }}
                    >
                      <Box
                        component="img"
                        src={g.icon}
                        alt=""
                        sx={{
                          width: 34,
                          height: 34,
                          objectFit: 'contain',
                          filter: st === 'pending' ? 'grayscale(1)' : 'none',
                          opacity: st === 'pending' ? 0.45 : 1,
                        }}
                      />
                      {st === 'done' && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: -5,
                            bottom: -5,
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: '#16a34a',
                            border: '2px solid #fff',
                            color: '#fff',
                            fontSize: 13,
                            fontWeight: 900,
                            display: 'grid',
                            placeItems: 'center',
                          }}
                        >
                          ✓
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 14.5,
                            fontWeight: 900,
                            color: st === 'pending' ? tokens.faint : 'text.primary',
                          }}
                        >
                          {g.label}
                        </Typography>
                        {st === 'active' ? (
                          <Box
                            sx={{
                              width: 15,
                              height: 15,
                              border: `2.5px solid ${tokens.teal}40`,
                              borderTopColor: tokens.teal,
                              borderRadius: '50%',
                              animation: 'vpspin 0.9s linear infinite',
                            }}
                          />
                        ) : (
                          <Box
                            component="span"
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 900,
                              px: 1,
                              py: '2px',
                              borderRadius: 999,
                              background:
                                st === 'done' ? '#e7f6ed' : st === 'error' ? '#fdeaea' : '#eef1f5',
                              color:
                                st === 'done'
                                  ? '#16a34a'
                                  : st === 'error'
                                    ? '#dc2626'
                                    : tokens.faint,
                            }}
                          >
                            {st === 'done' ? '完了' : st === 'error' ? 'エラー' : '待機中'}
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                        {g.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* 案内ノート */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.25,
                alignItems: 'flex-start',
                background: 'rgba(102,126,234,0.10)',
                border: `1px solid ${tokens.line}`,
                borderRadius: '14px',
                p: 2,
              }}
            >
              <InfoIcon sx={{ fontSize: 18, color: tokens.indigo, mt: '1px' }} />
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: 'text.primary' }}>
                  このまま画面を離れても大丈夫です。
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25, lineHeight: 1.55 }}
                >
                  処理はバックグラウンドで継続されます。完了後、一覧画面から詳細を確認できます。
                </Typography>
              </Box>
            </Box>

            {/* CTA（完了時のみ。処理中の「戻る」はヘッダーに集約） */}
            {isCompleted && (
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
                <Button
                  onClick={() => navigate(`/workflow/${itemId}`)}
                  sx={{
                    background: 'linear-gradient(120deg, #0d9488, #5b56e0)',
                    color: '#fff',
                    fontWeight: 800,
                    borderRadius: '12px',
                    px: 2.5,
                    py: 1.1,
                    textTransform: 'none',
                  }}
                >
                  抽出した主張を確認 →
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default VideoProcessing;
